// Tag — a calm, tinted chip naming a THING (a cluster, a country, a filter
// value), per COMPONENTS §Catalog. Sibling of Badge, split by JOB not by
// shape: Badge says STATUS (semantic-tinted — green/amber/red/blue family);
// Tag says a THING (neutral / brand tints only, removable machinery in
// TagGroup). A thing's status is written in words, never carried by the tag's
// colour.
//
// This file is the STATIC chip (read-only contexts: a role's clusters on a
// detail page) — presentational, so hand-rolled per ADR-028 — and, with
// `onClick`, the same chip as a DOOR: a real <button> with the same look (a
// folder chip that applies its folder filter — the mailbox-label idiom). The
// interactive removable collection (keyboard roving, X per chip, Backspace) is
// <TagGroup> (React Aria), which imports `tagClass` from here so the two
// render identically.

import type { ReactNode } from 'react';

export type TagVariant = 'neutral' | 'brand';

const variantClass: Record<TagVariant, string> = {
  neutral:
    'bg-[var(--color-bg-surface-alt)] border border-[var(--color-border)] text-[var(--color-text-primary)]',
  // 15%/40% of the DISPLAY value, like Badge's subtle recipe — never the
  // focus-ring token (solid since ADR-032).
  brand:
    'bg-[var(--color-primary-display)]/15 border border-[var(--color-primary-display)]/40 text-[var(--color-primary-display)]',
};

/** Shared chip recipe — consumed by TagGroup so static + removable tags match. */
export function tagClass(variant: TagVariant): string {
  return `inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${variantClass[variant]}`;
}

export interface TagProps {
  variant?: TagVariant;
  /** The thing's name (caller-supplied, i18n'd where applicable). */
  children: ReactNode;
  className?: string;
  /** A DOOR — the same chip as a `<button>` (a folder chip applying its filter). */
  onClick?: () => void;
  /** The door's accessible name when the visible words are not the act ("Show only Permits"). */
  ariaLabel?: string;
}

export function Tag({ variant = 'neutral', children, className = '', onClick, ariaLabel }: TagProps) {
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className={`${tagClass(variant)} cursor-pointer outline-none focus-ring hover:border-[var(--color-border-strong)] hover:bg-[var(--color-bg-hover)] ${className}`}
      >
        {children}
      </button>
    );
  }
  return <span className={`${tagClass(variant)} ${className}`}>{children}</span>;
}
